class DataFilter {
  private container: HTMLElement;
  private headerElement: HTMLElement | null;
  private contentElement: HTMLElement | null;
  private filterTriggers!: NodeListOf<HTMLElement>;
  private filterTargets!: NodeListOf<HTMLElement>;
  private defaultMessageElement: HTMLElement | null = null;
  private fallbackTitle = 'Zatím žádné komentáře.';

  constructor(container: HTMLElement) {
    this.container = container;
    this.headerElement = container.querySelector('[data-filter-header]');
    this.contentElement = container.querySelector('[data-filter-content]');

    if (!this.headerElement || !this.contentElement) {
      console.error('Required filter elements not found');
      return;
    }

    // Získání nebo vytvoření výchozího placeholderu
    const attr = this.headerElement.getAttribute('data-fallback-title');
    this.fallbackTitle = attr && attr.trim() ? attr.trim() : this.fallbackTitle;
    this.defaultMessageElement = this.contentElement.querySelector('[data-filter-default]');

    if (!this.defaultMessageElement) {
      this.defaultMessageElement = document.createElement('div');
      this.defaultMessageElement.setAttribute('data-filter-default', '');
      this.defaultMessageElement.className = 'filters__default-message';
      this.defaultMessageElement.style.display = 'none';
      this.contentElement.appendChild(this.defaultMessageElement);
    }

    this.filterTriggers = this.headerElement.querySelectorAll('[data-filter-trigger]');
    this.filterTargets = this.contentElement.querySelectorAll('[data-filter-target]');

    // Nastavení resetovacího filtru jako výchozí aktivní položky
    const resetTrigger = Array.from(this.filterTriggers).find(
      (trigger) => trigger.getAttribute('data-filter-trigger') === ''
    );

    if (resetTrigger) {
      resetTrigger.setAttribute('data-is-active', 'true');
    }

    // Zobrazení výchozího placeholderu, pokud nejsou k dispozici žádné položky filtru
    if (this.filterTargets.length === 0) {
      if (this.defaultMessageElement) {
        this.defaultMessageElement.textContent = this.fallbackTitle;
        this.defaultMessageElement.style.display = 'block';
      }
    }

    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    this.filterTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleFilterClick(trigger);
      });
    });
  }

  private handleFilterClick(clickedTrigger: HTMLElement): void {
    // Zrušení aktivního stavu všech položek filtru
    this.filterTriggers.forEach((trigger) => {
      trigger.removeAttribute('data-is-active');
    });

    // Nastavení aktivního stavu vybrané položky
    clickedTrigger.setAttribute('data-is-active', 'true');

    // Získání hodnoty a názvu vybraného filtru
    const filterValue = clickedTrigger.getAttribute('data-filter-trigger') || '';
    const filterLabel = filterValue !== 'reset' ? clickedTrigger.textContent : '';

    // Počet položek odpovídajících vybranému filtru
    let visibleItemCount = 0;

    // Filtrování obsahu podle vybrané hodnoty
    this.filterTargets.forEach((target) => {
      const targetValue = target.getAttribute('data-filter-target') || '';

      if (filterValue === 'reset' || targetValue === filterValue) {
        target.style.display = '';
        visibleItemCount++;
      } else {
        target.style.display = 'none';
      }
    });

    // Nastavení textu a viditelnosti výchozího placeholderu
    if (this.defaultMessageElement) {
      if (filterLabel) {
        this.defaultMessageElement.textContent =
          filterLabel.toLowerCase() === 'češi'
            ? 'V tento den nestartují čeští sportovci.'
            : `Zatím žádné ${filterLabel.toLowerCase()} zápasy.`;
      } else {
        this.defaultMessageElement.textContent = this.fallbackTitle;
      }

      if (visibleItemCount === 0) {
        this.defaultMessageElement.style.display = 'block';
      } else {
        this.defaultMessageElement.style.display = 'none';
      }
    }

    // Odeslání informace o změně filtru pro další navázané komponenty
    this.container.dispatchEvent(
      new CustomEvent('data-filter:change', {
        detail: {
          filterValue,
          filterLabel,
          visibleItemCount,
        },
      })
    );
  }

  public applyFilter(filterValue: string): void {
    const triggerToActivate = Array.from(this.filterTriggers).find(
      (trigger) => trigger.getAttribute('data-filter-trigger') === filterValue
    );

    if (triggerToActivate) {
      this.handleFilterClick(triggerToActivate);
    }
  }

  public resetFilter(): void {
    const resetTrigger = Array.from(this.filterTriggers).find(
      (trigger) => trigger.getAttribute('data-filter-trigger') === 'reset'
    );

    if (resetTrigger) {
      this.handleFilterClick(resetTrigger);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const filterContainers = document.querySelectorAll('[data-filter]');
  filterContainers.forEach((container) => {
    try {
      new DataFilter(container as HTMLElement);
    } catch (error) {
      console.error('Filter initialization error:', error);
    }
  });
});

export default DataFilter;
